import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react'
import { tourSteps } from '../../constants/tourSteps'
import { useLocation } from 'react-router-dom'
import MeService from '../../services/me-service'

import {
  setMiniSidenav,
  useAuthContext,
  useMaterialUIController,
} from '../index'
import { useMediaQuery } from 'react-responsive'
import { removeTutorialOverlay } from 'utils'
import { settings } from 'settings'

const TourContext = createContext()

export const useTourContext = () => {
  const context = useContext(TourContext)

  if (!context) {
    throw new Error('useTourContext must be used within a TourProvider')
  }

  return context
}

export const TourProvider = ({ children }) => {
  const [currentTourStep, setCurrentTourStep] = useState(0)
  const [isCloseModalOpened, setIsCloseModalOpened] = useState(false)
  const [focusTour, setFocusTour] = useState(true)
  const [_, dispatch] = useMaterialUIController()
  const { userDetails } = useAuthContext()
  const isMobile = useMediaQuery({
    query: '(max-width:1024px)',
  })
  const [isOpen, setIsOpen] = useState(false)
  const { setUserDetails } = useAuthContext()
  const { pathname } = useLocation()

  const hasPages = settings.pages_enabled
  const steps = useMemo(
    () =>
      tourSteps.filter(
        step =>
          (step.mobileOnly !== true || isMobile) &&
          (step.pagesOnly !== true || hasPages),
      ),
    [hasPages, isMobile],
  )

  const tourPaused = useMemo(
    () =>
      steps[currentTourStep]?.pause === true ||
      (steps[currentTourStep]?.url &&
        !pathname.includes(steps[currentTourStep]?.url)),
    [currentTourStep, steps, pathname],
  )

  const hintStepSelector = '.hint-modal'
  const answerStepSelector = '.content-container'
  const hintStepNumber = steps.findIndex(
    step => step.selector === hintStepSelector,
  )
  const answerStepNumber = steps.findIndex(
    step => step.selector === answerStepSelector,
  )

  const setStepToSelector = useCallback(
    selector => {
      const stepNumber = steps.findIndex(step => step.selector === selector)

      setCurrentTourStep(stepNumber)
    },
    [steps],
  )

  const handleNextTourStep = useCallback(() => {
    if (userDetails?.tutorial || tourPaused) return

    setCurrentTourStep(prev => {
      let nextStep
      if (prev === hintStepNumber - 1) {
        const hintIcon = document.querySelector(hintStepSelector)
        if (!hintIcon) {
          nextStep = prev + 2
        } else {
          nextStep = prev + 1
        }
      } else if (prev === answerStepNumber - 1) {
        const nextButton = document.querySelector('#explain-container')
        if (nextButton) {
          nextStep = prev + 3
        } else {
          nextStep = prev + 1
        }
      } else {
        nextStep = prev + 1
      }

      if (steps[nextStep].pause !== true) {
        let nextSelector = document.querySelector(steps[nextStep].selector)
        while (
          steps[nextStep].pause !== true &&
          !nextSelector &&
          nextStep < steps.length - 1
        ) {
          nextStep++
          nextSelector = document.querySelector(steps[nextStep].selector)
        }
      }

      return nextStep
    })
  }, [
    answerStepNumber,
    hintStepNumber,
    userDetails?.tutorial,
    tourPaused,
    steps,
  ])

  const handlePrevTourStep = useCallback(() => {
    if (currentTourStep > 1) {
      setCurrentTourStep(prev => {
        let prevStep = prev - 1

        if (steps[prevStep].pause !== true) {
          let prevSelector = document.querySelector(steps[prevStep].selector)
          while (
            steps[prevStep].pause !== true &&
            !prevSelector &&
            prevStep > 0
          ) {
            prevStep--
            prevSelector = document.querySelector(steps[prevStep].selector)
          }
        }

        return prevStep
      })
    }
  }, [currentTourStep, steps])

  const handleFocusTour = useCallback(() => {
    setFocusTour(true)
  }, [])

  const handleUnFocusTour = useCallback(() => {
    setFocusTour(false)
  }, [])

  const onCloseTutorial = async () => {
    removeTutorialOverlay()

    try {
      await MeService.updateTutorialState({ tutorial: true })
      setUserDetails(prev => ({ ...prev, tutorial: true }))
      setIsCloseModalOpened(false)
    } catch (error) {
      console.error(error)
    }
  }

  const handleCloseModal = useCallback(() => {
    handleFocusTour()
    setIsCloseModalOpened(false)
  }, [handleFocusTour])

  const hideNextButton = useMemo(
    () => steps[currentTourStep].showNext === false,
    [currentTourStep, steps],
  )

  const hidePrevButton = useMemo(
    () => steps[currentTourStep].showPrev === false,
    [currentTourStep, steps],
  )

  useEffect(() => {
    setIsOpen(!userDetails?.tutorial && userDetails?.hasDetails)
  }, [userDetails])

  return (
    <TourContext.Provider
      value={{
        currentTourStep,
        handleNextTourStep,
        handlePrevTourStep,
        isCloseModalOpened,
        setIsCloseModalOpened,
        steps,
        focusTour,
        setFocusTour,
        handleFocusTour,
        handleUnFocusTour,
        onCloseTutorial,
        handleCloseModal,
        hideNextButton,
        hidePrevButton,
        isOpen,
        tourPaused,
        setStepToSelector,
      }}
    >
      {children}
    </TourContext.Provider>
  )
}
